import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, List, Filter, ArrowDownUp, SlidersHorizontal, X, ChevronDown, ChevronLeft, Search, Check } from 'lucide-react';
import itemService from '../../services/itemService';
import catalogService from '../../services/catalogService';
import ProductCard from '../../components/common/ProductCard';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl, generateSlug } from '../../utils/helpers'; 
import { PRODUCT_STATUS } from '../../utils/constants';

const initFromSession = (key, defaultValue) => {
    if (typeof window === 'undefined') return defaultValue;
    const saved = sessionStorage.getItem(key);
    try {
        return saved ? JSON.parse(saved) : defaultValue;
    } catch {
        return defaultValue;
    }
};

const STATUS_WEIGHTS = {
    [PRODUCT_STATUS.IN_STOCK]: 1,
    [PRODUCT_STATUS.PRE_ORDER]: 2,
    [PRODUCT_STATUS.COMING_SOON]: 3,
    [PRODUCT_STATUS.OUT_OF_STOCK]: 4
};
const getStatusWeight = (status) => STATUS_WEIGHTS[status] || 5;

// 👉 TỐI ƯU LCP 1: Viết lại hàm stripHtml, gộp Regex để chạy nhanh gấp 3 lần, giải phóng Main Thread
const stripHtml = (html) => {
    if (!html) return '';
    return String(html)
        .replace(/<\/?[^>]+(>|$)/g, " ") // Bóc toàn bộ thẻ HTML trong 1 lần duyệt
        .replace(/&nbsp;/g, ' ') 
        .replace(/\s+/g, ' ') 
        .trim();
};

const formatNumberWithDots = (numStr) => {
    if (!numStr) return '';
    const cleanStr = String(numStr).replace(/\D/g, ''); 
    return cleanStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const MenuPage = () => {
    const { slug } = useParams(); 
    const menuId = slug ? slug.split('-id').pop() : null;
    
    const [products, setProducts] = useState([]);
    const [menus, setMenus] = useState([]); 
    const [adminSpecs, setAdminSpecs] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [loadingSidebar, setLoadingSidebar] = useState(true);

    const [selectedFilters, setSelectedFilters] = useState(() => initFromSession(`menu_${menuId}_specs`, {}));
    const [sortOrder, setSortOrder] = useState(() => initFromSession(`menu_${menuId}_sort`, 'default'));
    const [inStockOnly, setInStockOnly] = useState(() => initFromSession(`menu_${menuId}_stock`, false));
    const [minPrice, setMinPrice] = useState(() => initFromSession(`menu_${menuId}_minPrice`, ''));
    const [maxPrice, setMaxPrice] = useState(() => initFromSession(`menu_${menuId}_maxPrice`, ''));
    const [selectedBrands, setSelectedBrands] = useState(() => initFromSession(`menu_${menuId}_brands`, []));
    const [currentPage, setCurrentPage] = useState(() => initFromSession(`menu_${menuId}_page`, 1));

    const [openSpecDropdown, setOpenSpecDropdown] = useState(null);
    const [dropdownAlign, setDropdownAlign] = useState('left');
    const [openSortDropdown, setOpenSortDropdown] = useState(false); 
    const [expandedMenus, setExpandedMenus] = useState([]);
    const itemsPerPage = 12;

    const sortOptions = [
        { value: 'default', label: 'Nổi bật nhất' },
        { value: 'price_asc', label: 'Giá: Thấp đến Cao' },
        { value: 'price_desc', label: 'Giá: Cao đến Thấp' }
    ];

    useEffect(() => {
        if (!menuId) return;
        // Xóa bộ lọc cũ nếu người dùng chuyển menu
        const currentSavedSort = initFromSession(`menu_${menuId}_sort`, 'default');
        setSelectedFilters(initFromSession(`menu_${menuId}_specs`, {}));
        setSortOrder(currentSavedSort);
        setInStockOnly(initFromSession(`menu_${menuId}_stock`, false));
        setSelectedBrands(initFromSession(`menu_${menuId}_brands`, []));
        setMinPrice(initFromSession(`menu_${menuId}_minPrice`, ''));
        setMaxPrice(initFromSession(`menu_${menuId}_maxPrice`, ''));
        setCurrentPage(initFromSession(`menu_${menuId}_page`, 1));
        
        setOpenSpecDropdown(null);
        setOpenSortDropdown(false);
    }, [menuId]);

    useEffect(() => {
        if (!menuId) return;
        sessionStorage.setItem(`menu_${menuId}_specs`, JSON.stringify(selectedFilters));
        sessionStorage.setItem(`menu_${menuId}_sort`, JSON.stringify(sortOrder));
        sessionStorage.setItem(`menu_${menuId}_stock`, JSON.stringify(inStockOnly));
        sessionStorage.setItem(`menu_${menuId}_brands`, JSON.stringify(selectedBrands));
        sessionStorage.setItem(`menu_${menuId}_minPrice`, JSON.stringify(minPrice));
        sessionStorage.setItem(`menu_${menuId}_maxPrice`, JSON.stringify(maxPrice));
        sessionStorage.setItem(`menu_${menuId}_page`, currentPage.toString());
    }, [menuId, selectedFilters, sortOrder, inStockOnly, minPrice, maxPrice, selectedBrands, currentPage]);

    useEffect(() => {
        let isMounted = true;
        const fetchSidebarData = async () => {
            try {
                const [resMenus, resSpecs] = await Promise.all([
                    catalogService.getPublicMenus().catch(() => []),
                    catalogService.getSpecs().catch(() => [])
                ]);
                
                if (!isMounted) return;

                const menuData = Array.isArray(resMenus?.data) ? resMenus.data : (Array.isArray(resMenus) ? resMenus : []);
                setMenus(menuData.filter(m => m.menuPosition === 'CATEGORY').sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0)));

                const specsData = Array.isArray(resSpecs?.data) ? resSpecs.data : (Array.isArray(resSpecs) ? resSpecs : []);
                setAdminSpecs(specsData);
            } catch (error) {
                console.error("Lỗi lấy danh sách Sidebar:", error);
            } finally {
                if (isMounted) setLoadingSidebar(false);
            }
        };
        fetchSidebarData();
        return () => { isMounted = false; };
    }, []);

    const sortedCategoryMenus = useMemo(() => {
        const sortMenusRecursive = (menuList) => {
            if (!menuList || menuList.length === 0) return [];
            return [...menuList]
                .sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0))
                .map(m => ({
                    ...m,
                    subMenus: sortMenusRecursive(m.subMenus)
                }));
        };
        return sortMenusRecursive(menus);
    }, [menus]);

    useEffect(() => {
        let isMounted = true;
        const fetchMenuProducts = async () => {
            if (!menuId) return;
            setProducts([]);
            setLoading(true);
            try {
                const res = await itemService.getItemsByMenuId(menuId); 
                if (!isMounted) return;

                const productData = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                
                const preProcessedProducts = productData.map(p => {
                    const activePrice = (p.itemFlashSalePrice > 0 && p.itemFlashSaleQuantity > 0) 
                        ? p.itemFlashSalePrice 
                        : (p.itemSalePrice || p.itemReleasePrice || -1);

                    const statusWeight = getStatusWeight(p.itemStatus);

                    const specMap = {};
                    if (Array.isArray(p.itemSpecs)) {
                        p.itemSpecs.forEach(spec => {
                            const specName = spec.spec?.specName?.trim();
                            if (specName) {
                                specMap[specName] = stripHtml(spec.specValue);
                            }
                        });
                    }

                    return { ...p, _activePrice: activePrice, _statusWeight: statusWeight, _specMap: specMap };
                });

                setProducts(preProcessedProducts);
            } catch (error) {
                console.error("Lỗi lấy dữ liệu trang Menu:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        
        fetchMenuProducts();
        return () => { isMounted = false; };
    }, [menuId]); 

    const menuInfo = useMemo(() => {
        let currentMenuName = "Đang tải...", currentMenuIcon = null, parentMenuName = null, parentMenuId = null;
        if (sortedCategoryMenus.length > 0 && menuId) {
            const targetId = parseInt(menuId);
            const findPath = (menuList, currentPath) => {
                for (let menu of menuList) {
                    if (menu.menuId === targetId) return [...currentPath, menu.menuId];
                    if (menu.subMenus?.length > 0) {
                        const found = findPath(menu.subMenus, [...currentPath, menu.menuId]);
                        if (found) return found;
                    }
                }
                return null;
            };

            const path = findPath(sortedCategoryMenus, []);
            if (path) setExpandedMenus(prev => Array.from(new Set([...prev, ...path])));

            for (let cat of sortedCategoryMenus) {
                if (cat.menuId === targetId) {
                    currentMenuName = cat.menuName; currentMenuIcon = cat.menuIcon; break;
                }
                if (cat.subMenus) {
                    for (let sub of cat.subMenus) {
                        if (sub.menuId === targetId) {
                            currentMenuName = sub.menuName; currentMenuIcon = sub.menuIcon;
                            parentMenuName = cat.menuName; parentMenuId = cat.menuId; break;
                        }
                        if (sub.subMenus) {
                            for (let sub3 of sub.subMenus) {
                                if (sub3.menuId === targetId) {
                                    currentMenuName = sub3.menuName; currentMenuIcon = sub3.menuIcon;
                                    parentMenuName = cat.menuName; parentMenuId = cat.menuId; break;
                                }
                                if (sub3.subMenus) {
                                    let foundSub4 = sub3.subMenus.find(s4 => s4.menuId === targetId);
                                    if (foundSub4) {
                                        currentMenuName = foundSub4.menuName; currentMenuIcon = foundSub4.menuIcon;
                                        parentMenuName = cat.menuName; parentMenuId = cat.menuId; break;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return { currentMenuName, currentMenuIcon, parentMenuName, parentMenuId };
    }, [sortedCategoryMenus, menuId]);

    useEffect(() => {
        if (menuInfo.currentMenuName && menuInfo.currentMenuName !== "Đang tải...") {
            document.title = `${menuInfo.currentMenuName} | Thành Nam PC`;
            let metaDescriptionTag = document.querySelector('meta[name="description"]');
            if (metaDescriptionTag) {
                const dynamicDescription = `Mua ${menuInfo.currentMenuName} chính hãng với giá rẻ hàng đầu Quảng Ninh. Đặt hàng online đơn giản, giao hàng tận nhà nhanh chóng. Dịch vụ sau bán hàng uy tín, chuyên nghiệp.`;
                metaDescriptionTag.setAttribute("content", dynamicDescription);
            }
        }
    }, [menuInfo.currentMenuName]);
    
    const toggleExpand = useCallback((e, mId) => {
        e.preventDefault(); e.stopPropagation();
        setExpandedMenus(prev => prev.includes(mId) ? prev.filter(id => id !== mId) : [...prev, mId]);
    }, []);

    const availableBrands = useMemo(() => {
        if (products.length === 0) return [];
        const brandMap = new Map();
        products.forEach(p => {
            if (p.brand?.brandId && !brandMap.has(p.brand.brandId)) {
                brandMap.set(p.brand.brandId, p.brand);
            }
        });
        return Array.from(brandMap.values());
    }, [products]);

    const handleBrandChange = useCallback((brandId) => {
        setSelectedBrands(prev => prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]);
        setCurrentPage(1);
    }, []);

    const availableSpecs = useMemo(() => {
        if (products.length === 0 || adminSpecs.length === 0) return [];
        
        const uniqueCategoryIds = new Set();
        products.forEach(p => {
            const catId = p.categoryId || p.deviceCategory?.deviceCategoryId;
            if (catId) uniqueCategoryIds.add(catId);
        });
        if (uniqueCategoryIds.size !== 1) return [];
        const targetCategoryId = Array.from(uniqueCategoryIds)[0];

        const relevantAdminSpecs = adminSpecs.filter(s => {
            const adminCatId = s.categoryId || s.deviceCategory?.deviceCategoryId;
            return String(adminCatId) === String(targetCategoryId) && s.isFilterable === true;
        });

        const activeValuesMap = {};
        products.forEach(p => {
            if (p._specMap) {
                for (const [specName, specValue] of Object.entries(p._specMap)) {
                    if (specValue) {
                        if (!activeValuesMap[specName]) activeValuesMap[specName] = new Set();
                        activeValuesMap[specName].add(specValue);
                    }
                }
            }
        });

        const formattedSpecs = [];
        relevantAdminSpecs.forEach(adminSpec => {
            const specName = adminSpec.specName?.trim();
            if (activeValuesMap[specName] && activeValuesMap[specName].size > 0) {
                formattedSpecs.push({
                    name: specName,
                    values: Array.from(activeValuesMap[specName]).sort()
                });
            }
        });

        return formattedSpecs;
    }, [products, adminSpecs]);

    const handleFilterChange = useCallback((specName, specValue) => {
        setSelectedFilters(prev => {
            const currentValues = prev[specName] || [];
            if (currentValues.includes(specValue)) {
                const newValues = currentValues.filter(v => v !== specValue);
                if (newValues.length === 0) {
                    const newState = { ...prev };
                    delete newState[specName];
                    return newState;
                }
                return { ...prev, [specName]: newValues };
            } else {
                return { ...prev, [specName]: [...currentValues, specValue] };
            }
        });
        setCurrentPage(1);
    }, []);

    const clearAllFilters = useCallback(() => {
        setSelectedFilters({}); setSortOrder('default'); setInStockOnly(false);
        setMinPrice(''); setMaxPrice(''); setSelectedBrands([]);
        setOpenSpecDropdown(null); setCurrentPage(1);
    }, []);

    const handlePriceInput = (setter) => (e) => setter(formatNumberWithDots(e.target.value));
    const handlePriceBlur = (val, setter) => () => {
        if (!val) return;
        let cleanNum = parseInt(val.replace(/\./g, ''), 10);
        if (cleanNum > 0 && cleanNum < 1000) cleanNum = cleanNum * 1000;
        setter(formatNumberWithDots(cleanNum.toString()));
        setCurrentPage(1);
    };

    const displayedProducts = useMemo(() => {
        let result = [...products];

        if (typeof selectedFilters !== 'undefined' && Object.keys(selectedFilters).length > 0) {
            result = result.filter(p => {
                for (const [specName, allowedValues] of Object.entries(selectedFilters)) {
                    const productValue = p._specMap ? p._specMap[specName] : undefined;
                    if (!productValue || !allowedValues.includes(productValue)) return false;
                }
                return true;
            });
        }

        if (selectedBrands.length > 0) {
            result = result.filter(p => p.brand && selectedBrands.includes(p.brand.brandId));
        }

        if (inStockOnly) {
            result = result.filter(p => p.itemQuantity > 0 && p.itemStatus === PRODUCT_STATUS.IN_STOCK);
        }

        const currentMin = minPrice !== '' ? parseInt(minPrice.replace(/\./g, '')) : 0;
        const currentMax = maxPrice !== '' ? parseInt(maxPrice.replace(/\./g, '')) : Infinity;
        
        if (currentMin > 0 || currentMax !== Infinity) {
            result = result.filter(p => p._activePrice >= currentMin && p._activePrice <= currentMax);
        }

        if (sortOrder === 'price_asc' || sortOrder === 'price_desc') {
            result.sort((a, b) => {
                if (a._activePrice < 0 && b._activePrice < 0) return 0;
                if (a._activePrice < 0) return 1;
                if (b._activePrice < 0) return -1;
                return sortOrder === 'price_asc' ? a._activePrice - b._activePrice : b._activePrice - a._activePrice;
            });
        } else {
            result.sort((a, b) => {
                const prioA = a.priority || 0;
                const prioB = b.priority || 0;
                if (prioA !== prioB) return prioB - prioA; 
                if (a._statusWeight !== b._statusWeight) return a._statusWeight - b._statusWeight;
                return (b.itemId || 0) - (a.itemId || 0); 
            });
        }

        return result;
    }, [products, sortOrder, inStockOnly, selectedFilters, selectedBrands, minPrice, maxPrice]);

    const totalPages = Math.ceil(displayedProducts.length / itemsPerPage);
    const safeCurrentPage = Math.min(currentPage, Math.max(totalPages, 1));
    const paginatedProducts = displayedProducts.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage);

    const handlePageChange = useCallback((pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    }, [totalPages]);

    const getPaginationRange = () => {
        const delta = 2; const range = [];
        for (let i = Math.max(2, safeCurrentPage - delta); i <= Math.min(totalPages - 1, safeCurrentPage + delta); i++) range.push(i);
        if (safeCurrentPage - delta > 2) range.unshift("...");
        if (safeCurrentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            <main className="flex-grow pb-12">
                <div className="bg-gradient-to-r from-orange-100 via-orange-50 to-white border-b border-orange-200/60 sticky top-0 z-40 shadow-sm">
                    <div className="container mx-auto px-4 py-3.5 text-[13px] md:text-sm flex items-center gap-1.5 md:gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide min-h-[48px] md:min-h-[52px]">
                        <Link to="/" className="text-gray-600 font-bold hover:text-tnc-orange transition-colors">Trang chủ</Link>
                        <ChevronRight size={14} className="flex-shrink-0 text-gray-400" />
                        {menuInfo.parentMenuName && (
                            <>
                                <Link to={`/danh-muc/${generateSlug(menuInfo.parentMenuName)}-id${menuInfo.parentMenuId}`} className="text-gray-600 font-bold hover:text-tnc-orange transition-colors">{menuInfo.parentMenuName}</Link>
                                <ChevronRight size={14} className="flex-shrink-0 text-gray-400" />
                            </>
                        )}
                        <span className="font-extrabold text-gray-900 truncate">{menuInfo.currentMenuName}</span>
                    </div>
                </div>

                <div className="container mx-auto px-4 mt-6 flex flex-col lg:flex-row gap-6">
                    
                    <aside className="w-full lg:w-[280px] xl:w-[320px] flex-shrink-0 flex flex-col gap-6">
                        <div className="bg-gradient-to-b from-orange-100 to-orange-50 p-5 rounded-xl shadow-sm border border-orange-300/60 sticky top-28">
                            <h3 className="font-extrabold text-gray-800 text-lg mb-4 flex items-center gap-2 border-b pb-3 border-orange-300/50">
                                <List size={20} className="text-tnc-orange"/> Danh mục sản phẩm
                            </h3>
                            
                            <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto pr-1 custom-scrollbar">
                                {loadingSidebar ? (
                                    <div className="flex flex-col gap-3 mt-2">
                                        {[...Array(6)].map((_, i) => (
                                            <div key={i} className="h-10 bg-orange-200/60 rounded-lg animate-pulse w-full"></div>
                                        ))}
                                    </div>
                                ) : sortedCategoryMenus.map(cat => {
                                    const isParentActive = parseInt(menuId) === cat.menuId;
                                    const hasActiveChild = cat.subMenus?.some(sub => 
                                        sub.menuId === parseInt(menuId) || 
                                        sub.subMenus?.some(s3 => 
                                            s3.menuId === parseInt(menuId) || 
                                            s3.subMenus?.some(s4 => s4.menuId === parseInt(menuId))
                                        )
                                    );
                                    const hasChildren = cat.subMenus && cat.subMenus.length > 0;
                                    const isExpanded = expandedMenus.includes(cat.menuId);

                                    return (
                                        <div key={cat.menuId} className="flex flex-col mb-1">
                                            <div className={`flex items-start justify-between rounded-lg transition-all border ${isParentActive || hasActiveChild ? 'bg-white/80 shadow-sm border-white' : 'border-transparent hover:bg-white/50'}`}>
                                                <Link 
                                                    id={`sidebar-item-${cat.menuId}`}
                                                    to={`/danh-muc/${generateSlug(cat.menuName)}-id${cat.menuId}`}
                                                    className={`flex-1 flex items-center gap-2.5 py-1.5 px-3 ${isParentActive || hasActiveChild ? 'text-tnc-orange font-black text-[15px]' : 'text-gray-800 font-extrabold text-[15px] hover:text-tnc-orange'}`}
                                                >
                                                    {cat.menuIcon && <img src={getImageUrl(cat.menuIcon)} alt="" className="w-6 h-6 object-contain shrink-0" />}
                                                    <span className="whitespace-normal break-words leading-snug">{cat.menuName}</span>
                                                </Link>
                                                
                                                {hasChildren && (
                                                    <button onClick={(e) => toggleExpand(e, cat.menuId)} className="p-2 mr-1 mt-1 text-gray-500 hover:text-tnc-orange hover:bg-white/60 rounded-md transition-colors shrink-0">
                                                        <ChevronDown size={16} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180 text-tnc-orange' : '-rotate-90'}`} />
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {isExpanded && hasChildren && (
                                                <div className="flex flex-col ml-4 mt-1 space-y-1 border-l-2 border-orange-200 pl-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    {cat.subMenus.map(sub => {
                                                        const isSubActive = parseInt(menuId) === sub.menuId;
                                                        const hasActiveSub3 = sub.subMenus?.some(s3 => 
                                                            s3.menuId === parseInt(menuId) ||
                                                            s3.subMenus?.some(s4 => s4.menuId === parseInt(menuId))
                                                        );
                                                        const hasSubChildren = sub.subMenus && sub.subMenus.length > 0;
                                                        const isSubExpanded = expandedMenus.includes(sub.menuId);

                                                        return (
                                                            <div key={sub.menuId} className="flex flex-col w-full">
                                                                <div className={`flex items-start justify-between rounded-lg transition-all relative border ${isSubActive || hasActiveSub3 ? 'bg-white/70 shadow-sm border-white/50' : 'border-transparent hover:bg-white/40'}`}>
                                                                    {(isSubActive || hasActiveSub3) && <span className="absolute -left-[14px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-tnc-orange shadow-sm"></span>}
                                                                    <Link 
                                                                        id={`sidebar-item-${sub.menuId}`}
                                                                        to={`/danh-muc/${generateSlug(sub.menuName)}-id${sub.menuId}`}
                                                                        className={`flex-1 flex items-center gap-2 py-1.5 px-2 ${isSubActive || hasActiveSub3 ? 'font-black text-tnc-orange text-[14px]' : 'font-bold text-gray-700 text-[14px] hover:text-tnc-orange'}`}
                                                                    >
                                                                        {sub.menuIcon && <img src={getImageUrl(sub.menuIcon)} alt="" className="w-6 h-6 object-contain shrink-0" />}
                                                                        <span className="whitespace-normal break-words leading-snug">{sub.menuName}</span>
                                                                    </Link>
                                                                    
                                                                    {hasSubChildren && (
                                                                        <button onClick={(e) => toggleExpand(e, sub.menuId)} className="p-1.5 mr-1 mt-0.5 text-gray-500 hover:text-tnc-orange hover:bg-white/60 rounded-md transition-colors shrink-0">
                                                                            <ChevronDown size={14} className={`transition-transform duration-300 ${isSubExpanded ? 'rotate-180 text-tnc-orange' : '-rotate-90'}`} />
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {isSubExpanded && hasSubChildren && (
                                                                    <div className="flex flex-col ml-3 mt-0.5 space-y-1 animate-in fade-in slide-in-from-top-1 duration-300">
                                                                        {sub.subMenus.map(sub3 => {
                                                                            const isSub3Active = parseInt(menuId) === sub3.menuId;
                                                                            const hasActiveSub4 = sub3.subMenus?.some(s4 => s4.menuId === parseInt(menuId));
                                                                            const hasSub3Children = sub3.subMenus && sub3.subMenus.length > 0;
                                                                            const isSub3Expanded = expandedMenus.includes(sub3.menuId);

                                                                            return (
                                                                                <div key={sub3.menuId} className="flex flex-col w-full">
                                                                                    <div className={`flex items-start justify-between rounded-lg transition-all ${isSub3Active || hasActiveSub4 ? 'bg-white/60 shadow-sm border border-white/40' : 'hover:bg-white/30 border border-transparent'}`}>
                                                                                        <Link 
                                                                                            id={`sidebar-item-${sub3.menuId}`}
                                                                                            to={`/danh-muc/${generateSlug(sub3.menuName)}-id${sub3.menuId}`}
                                                                                            className={`flex-1 flex items-center gap-1.5 py-1 px-2 transition-all ${isSub3Active || hasActiveSub4 ? 'font-bold text-tnc-orange text-[13px]' : 'font-semibold text-gray-600 text-[13px] hover:text-tnc-orange'}`}
                                                                                        >
                                                                                            <span className={`leading-none shrink-0 ${isSub3Active || hasActiveSub4 ? 'text-tnc-orange font-black' : 'text-gray-400'}`}>
                                                                                                {isSub3Active || hasActiveSub4 ? '|' : '-'}
                                                                                            </span>
                                                                                            {sub3.menuIcon && <img src={getImageUrl(sub3.menuIcon)} alt="" className="w-6 h-6 object-contain shrink-0" />}
                                                                                            <span className="whitespace-normal break-words leading-snug">{sub3.menuName}</span>
                                                                                        </Link>

                                                                                        {hasSub3Children && (
                                                                                            <button onClick={(e) => toggleExpand(e, sub3.menuId)} className="p-1 text-gray-400 hover:text-tnc-orange rounded-md transition-colors shrink-0">
                                                                                                <ChevronDown size={14} className={`transition-transform duration-300 ${isSub3Expanded ? 'rotate-180 text-tnc-orange' : '-rotate-90'}`} />
                                                                                            </button>
                                                                                        )}
                                                                                    </div>

                                                                                    {isSub3Expanded && hasSubChildren && (
                                                                                        <div className="flex flex-col ml-4 mt-0.5 space-y-1 border-l border-orange-200/50 pl-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                                                                            {sub3.subMenus.map(sub4 => {
                                                                                                const isSub4Active = parseInt(menuId) === sub4.menuId;
                                                                                                return (
                                                                                                    <Link 
                                                                                                        key={sub4.menuId} 
                                                                                                        id={`sidebar-item-${sub4.menuId}`} 
                                                                                                        to={`/danh-muc/${generateSlug(sub4.menuName)}-id${sub4.menuId}`} 
                                                                                                        className={`flex items-center gap-1.5 text-[13px] py-1 px-2 rounded transition-all ${isSub4Active ? 'font-bold text-tnc-orange bg-white/50 shadow-sm border border-white/30' : 'font-medium text-gray-600 hover:text-tnc-orange hover:bg-white/30 border border-transparent'}`}
                                                                                                    >
                                                                                                        <span className={`leading-none shrink-0 ${isSub4Active ? 'text-tnc-orange font-black' : 'text-gray-300'}`}>•</span>
                                                                                                        {sub4.menuIcon && <img src={getImageUrl(sub4.menuIcon)} alt="" className="w-5 h-5 object-contain shrink-0" />}
                                                                                                        <span className="whitespace-normal break-words leading-snug">{sub4.menuName}</span>
                                                                                                    </Link>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </aside>

                    <section className="flex-1 min-w-0 flex flex-col min-h-[70vh]">
                        {/* 👉 TỐI ƯU CLS 1: Thêm min-h và Skeletons cho Khu vực Tiêu đề & Bộ Lọc */}
                        <div className="bg-gradient-to-r from-orange-100 to-orange-50 p-4 md:p-5 rounded-xl shadow-sm border border-orange-300/60 mb-4">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 border-b border-orange-300/50 pb-4 min-h-[48px]">
                                <h1 className="text-lg font-bold text-gray-800 flex items-center flex-wrap gap-2 w-full">
                                    {loading ? (
                                        <div className="flex gap-2 items-center w-full max-w-sm animate-pulse">
                                            <div className="w-8 h-8 bg-orange-200 rounded-full shrink-0"></div>
                                            <div className="h-6 bg-orange-200 rounded w-1/2"></div>
                                            <div className="h-6 w-24 bg-orange-200 rounded-full ml-auto md:ml-2"></div>
                                        </div>
                                    ) : (
                                        <>
                                            {menuInfo.currentMenuIcon && <img src={getImageUrl(menuInfo.currentMenuIcon)} alt="" className="w-8 h-8 object-contain" />}
                                            {menuInfo.currentMenuName} 
                                            <span className="bg-white border border-orange-200/60 text-tnc-orange px-3 py-1 rounded-full text-sm font-black shadow-sm">{displayedProducts.length} sản phẩm</span>
                                        </>
                                    )}
                                </h1>
                                {!loading && (Object.keys(selectedFilters).length > 0 || minPrice !== '' || maxPrice !== '' || sortOrder !== 'default' || inStockOnly || selectedBrands.length > 0) && (
                                    <button onClick={clearAllFilters} className="text-sm text-tnc-orange font-bold hover:underline flex items-center gap-1 bg-white/60 px-3 py-1.5 rounded-lg border border-orange-200/60 shadow-sm shrink-0">
                                        <X size={16}/> Xóa tất cả bộ lọc
                                    </button>
                                )}
                            </div>

                            {loading ? (
                                <div className="space-y-4 animate-pulse">
                                    <div className="flex gap-2"><div className="h-10 w-24 bg-orange-200/60 rounded-lg"></div><div className="h-10 w-24 bg-orange-200/60 rounded-lg"></div></div>
                                    <div className="flex gap-2"><div className="h-10 w-32 bg-orange-200/60 rounded-lg"></div><div className="h-10 w-48 bg-orange-200/60 rounded-lg"></div></div>
                                </div>
                            ) : (
                                <>
                                    {availableBrands.length > 0 && (
                                        <div className="mb-5">
                                            <div className="font-bold text-sm text-gray-700 mb-2">Thương hiệu:</div>
                                            <div className="flex flex-wrap gap-2">
                                                {availableBrands.map(brand => {
                                                    const isSelected = selectedBrands.includes(brand.brandId);
                                                    return (
                                                        <button
                                                            key={brand.brandId}
                                                            onClick={() => handleBrandChange(brand.brandId)}
                                                            className={`h-12 w-24 px-2 bg-white border rounded-lg flex items-center justify-center transition-all ${
                                                                isSelected ? 'border-tnc-orange ring-1 ring-tnc-orange shadow-md scale-105 bg-orange-50' : 'border-orange-200 hover:border-tnc-orange hover:shadow-sm'
                                                            }`}
                                                            title={brand.brandName}
                                                        >
                                                            {brand.brandImageUrl ? (
                                                                <img src={getImageUrl(brand.brandImageUrl)} alt={brand.brandName} className="max-h-10 px-1 w-full object-contain scale-110" />
                                                            ) : (
                                                                <span className={`text-xs font-bold truncate ${isSelected ? 'text-tnc-orange' : 'text-gray-600'}`}>{brand.brandName}</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-center gap-3 text-sm">
                                        <div className="flex items-center gap-2 text-gray-700">
                                            <Filter size={16} className="text-tnc-orange" />
                                            <span className="font-bold hidden sm:inline">Lọc cơ bản:</span>
                                        </div>

                                        <div className="relative">
                                            <button 
                                                onClick={() => setOpenSortDropdown(!openSortDropdown)}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors shadow-sm ${
                                                    sortOrder !== 'default' ? 'border-tnc-orange text-white bg-tnc-orange shadow-md' : 'border-orange-200 text-gray-700 bg-white hover:border-tnc-orange hover:text-tnc-orange'
                                                }`}
                                            >
                                                <ArrowDownUp size={14} className={sortOrder !== 'default' ? "text-white" : "text-gray-500"} />
                                                {sortOptions.find(o => o.value === sortOrder)?.label || 'Sắp xếp'}
                                                <ChevronDown size={14} className={`transition-transform ${openSortDropdown ? 'rotate-180' : ''}`}/>
                                            </button>

                                            {openSortDropdown && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setOpenSortDropdown(false)}></div>
                                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-orange-200/60 rounded-xl shadow-xl shadow-orange-100/50 z-50 p-2 flex flex-col gap-1">
                                                        {sortOptions.map(option => {
                                                            const isChecked = sortOrder === option.value;
                                                            return (
                                                                <button 
                                                                    key={option.value}
                                                                    onClick={() => { setSortOrder(option.value); setOpenSortDropdown(false); setCurrentPage(1); }}
                                                                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                                                                        isChecked ? 'bg-orange-50 font-bold text-tnc-orange' : 'text-gray-600 hover:bg-orange-50 hover:text-tnc-orange'
                                                                    }`}
                                                                >
                                                                    <span>{option.label}</span>
                                                                    {isChecked && <Check size={16} className="text-tnc-orange" />}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center border border-orange-200 rounded-lg bg-white overflow-hidden focus-within:border-tnc-orange focus-within:ring-1 focus-within:ring-tnc-orange transition-all shadow-sm">
                                                <input 
                                                    type="text" placeholder="Giá thấp nhất..." value={minPrice}
                                                    onChange={handlePriceInput(setMinPrice)}
                                                    onBlur={handlePriceBlur(minPrice, setMinPrice)}
                                                    className="w-28 sm:w-32 px-3 py-1.5 bg-transparent outline-none text-gray-700 font-medium text-sm"
                                                />
                                            </div>
                                            <span className="text-orange-400 font-bold">-</span>
                                            <div className="flex items-center border border-orange-200 rounded-lg bg-white overflow-hidden focus-within:border-tnc-orange focus-within:ring-1 focus-within:ring-tnc-orange transition-all shadow-sm">
                                                <input 
                                                    type="text" placeholder="Giá cao nhất..." value={maxPrice}
                                                    onChange={handlePriceInput(setMaxPrice)}
                                                    onBlur={handlePriceBlur(maxPrice, setMaxPrice)}
                                                    className="w-28 sm:w-32 px-3 py-1.5 bg-transparent outline-none text-gray-700 font-medium text-sm"
                                                />
                                            </div>
                                        </div>

                                        <label className="flex items-center gap-2 cursor-pointer border border-orange-200 bg-white shadow-sm rounded-lg px-3 py-1.5 hover:border-tnc-orange hover:bg-orange-50/50 transition-all ml-auto xl:ml-0">
                                            <input 
                                                type="checkbox" checked={inStockOnly} 
                                                onChange={(e) => { setInStockOnly(e.target.checked); setCurrentPage(1); }} 
                                                className="w-4 h-4 accent-tnc-orange cursor-pointer"
                                            />
                                            <span className="font-bold text-gray-700">Chỉ hiện Sẵn hàng</span>
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>

                        {!loading && availableSpecs.length > 0 && (
                            <div className="bg-gradient-to-r from-orange-100 to-orange-50 p-4 md:p-5 rounded-xl shadow-sm border border-orange-300/60 mb-4 flex flex-wrap gap-3 items-center">
                                <div className="flex items-center gap-2 text-gray-800 mr-1">
                                    <SlidersHorizontal size={16} className="text-tnc-orange"/>
                                    <span className="font-extrabold text-sm uppercase tracking-wide">Lọc cấu hình:</span>
                                </div>
                                
                                {availableSpecs.map((specGroup, idx) => {
                                    const selectedCount = selectedFilters[specGroup.name]?.length || 0;
                                    const isOpen = openSpecDropdown === specGroup.name;

                                    return (
                                        <div key={idx} className="relative">
                                            <button 
                                                onClick={(e) => {
                                                    if (isOpen) {
                                                        setOpenSpecDropdown(null);
                                                    } else {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const isRightHalf = (rect.left + rect.width / 2) > (window.innerWidth / 2);
                                                        setDropdownAlign(isRightHalf ? 'right' : 'left');
                                                        setOpenSpecDropdown(specGroup.name);
                                                    }
                                                }}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors shadow-sm ${selectedCount > 0 ? 'border-tnc-orange text-white bg-tnc-orange shadow-md' : 'border-orange-200 text-gray-700 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange bg-white'}`}
                                            >
                                                {specGroup.name}: {selectedCount === 0 ? 'Tất cả' : `${selectedCount} đã chọn`}
                                                <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
                                            </button>

                                            {isOpen && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setOpenSpecDropdown(null)}></div>
                                                    <div className={`absolute top-full mt-2 w-[320px] md:w-[450px] max-w-[90vw] bg-white border border-orange-200/60 rounded-xl shadow-xl shadow-orange-100/50 z-50 p-4 flex flex-col gap-3 ${dropdownAlign === 'right' ? 'right-0' : 'left-0'}`}>
                                                        <div className="max-h-60 overflow-y-auto pr-2 custom-scrollbar grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                                                            {specGroup.values.map((val, vIdx) => {
                                                                const isChecked = selectedFilters[specGroup.name]?.includes(val) || false;
                                                                return (
                                                                    <label key={vIdx} className="flex items-start gap-2 cursor-pointer group py-1.5 hover:bg-orange-50/50 px-2 rounded transition-colors break-words">
                                                                        <input 
                                                                            type="checkbox" checked={isChecked}
                                                                            onChange={() => handleFilterChange(specGroup.name, val)}
                                                                            className="mt-0.5 w-4 h-4 accent-tnc-orange cursor-pointer shrink-0"
                                                                        />
                                                                        <span className={`text-sm transition-colors ${isChecked ? 'font-bold text-tnc-orange' : 'text-gray-600 group-hover:text-tnc-orange font-medium'}`}>
                                                                            {val}
                                                                        </span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>

                                                        <div className="border-t border-orange-100 pt-3 flex justify-between items-center mt-1">
                                                            {selectedCount > 0 ? (
                                                                <button 
                                                                    onClick={() => {
                                                                        const newState = { ...selectedFilters };
                                                                        delete newState[specGroup.name];
                                                                        setSelectedFilters(newState);
                                                                        setCurrentPage(1);
                                                                    }}
                                                                    className="text-xs text-gray-500 hover:text-tnc-orange-hover font-bold hover:underline"
                                                                >
                                                                    Bỏ chọn
                                                                </button>
                                                            ) : <div></div>}
                                                            <button 
                                                                onClick={() => setOpenSpecDropdown(null)}
                                                                className="text-xs font-bold bg-tnc-orange text-white px-4 py-2 rounded-lg hover:bg-tnc-orange-hover transition-colors shadow-sm"
                                                            >
                                                                Xong
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {!loading && (Object.keys(selectedFilters).length > 0 || selectedBrands.length > 0) && (
                            <div className="flex flex-wrap gap-2 mb-6 p-3 bg-orange-100/50 rounded-xl border border-dashed border-orange-300 w-full shadow-inner">
                                <span className="text-xs font-bold text-gray-500 mt-2">Đang lọc:</span>
                                {selectedBrands.map(brandId => {
                                    const b = availableBrands.find(br => br.brandId === brandId);
                                    if(!b) return null;
                                    return (
                                        <span key={`brand-${brandId}`} className="flex items-center gap-1.5 bg-white border border-orange-200 text-tnc-orange px-3 py-1.5 rounded-full text-sm font-bold shadow-sm animate-in fade-in">
                                            Hãng: {b.brandName}
                                            <X size={14} className="cursor-pointer hover:text-white hover:bg-red-500 bg-orange-100 rounded-full p-0.5 transition-colors" onClick={() => handleBrandChange(brandId)}/>
                                        </span>
                                    );
                                })}

                                {Object.entries(selectedFilters).map(([sName, sValues]) => 
                                    sValues.map(val => (
                                        <span key={`${sName}-${val}`} className="flex items-center gap-1.5 bg-white border border-orange-200 text-tnc-orange px-3 py-1.5 rounded-full text-sm font-bold shadow-sm animate-in fade-in">
                                            {sName}: {val}
                                            <X size={14} className="cursor-pointer hover:text-white hover:bg-red-500 bg-orange-100 rounded-full p-0.5 transition-colors" onClick={() => handleFilterChange(sName, val)}/>
                                        </span>
                                    ))
                                )}
                            </div>
                        )}

                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-xl p-2.5 border border-orange-100 shadow-sm flex flex-col min-h-[390px] sm:min-h-[410px]">
                                        <div className="flex justify-between mb-2 mt-1">
                                            <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
                                            <div className="w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
                                        </div>
                                        <div className="w-full pb-[100%] bg-gray-200 rounded-lg animate-pulse mb-3"></div>
                                        <div className="w-full h-4 bg-gray-200 rounded animate-pulse mb-1.5"></div>
                                        <div className="w-2/3 h-4 bg-gray-200 rounded animate-pulse mb-3"></div>
                                        <div className="flex flex-wrap gap-1.5 mb-2 h-[50px]">
                                            <div className="w-1/3 h-5 bg-gray-100 rounded animate-pulse"></div>
                                            <div className="w-1/4 h-5 bg-gray-100 rounded animate-pulse"></div>
                                        </div>
                                        <div className="w-1/2 h-5 bg-gray-200 rounded animate-pulse mt-auto mb-3"></div>
                                        <div className="border-t border-gray-100 pt-3 flex justify-between items-center mt-1">
                                            <div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
                                            <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : displayedProducts.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {paginatedProducts.map((product, index) => (
                                        <ProductCard 
                                            key={product.itemId} 
                                            product={product} 
                                            getImageUrl={getImageUrl} 
                                            adminSpecs={adminSpecs} 
                                            // 👉 TỐI ƯU LCP 2: Báo hiệu cho ProductCard ưu tiên tải ảnh đầu tiên
                                            priority={index === 0} 
                                        />
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 border-t border-orange-200/60 pt-6 mt-auto">
                                        <div className="text-sm text-gray-500 font-medium">
                                            Hiển thị <strong className="text-gray-900">{(safeCurrentPage - 1) * itemsPerPage + 1} - {Math.min(safeCurrentPage * itemsPerPage, displayedProducts.length)}</strong> trên tổng số <strong className="text-gray-900">{displayedProducts.length}</strong> sản phẩm
                                        </div>

                                        <div className="flex flex-wrap items-center justify-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    onClick={() => handlePageChange(safeCurrentPage - 1)} 
                                                    disabled={safeCurrentPage === 1}
                                                    className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                                >
                                                    <ChevronLeft size={16} /> <span className="hidden sm:inline">Trang trước</span>
                                                </button>

                                                <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] md:max-w-none scrollbar-hide">
                                                    {getPaginationRange().map((page, index) => {
                                                        if (page === "...") {
                                                            return <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 font-bold">...</span>;
                                                        }
                                                        return (
                                                            <button 
                                                                key={index} 
                                                                onClick={() => handlePageChange(page)}
                                                                className={`w-10 h-10 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                                    ${safeCurrentPage === page 
                                                                        ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                                        : 'bg-white text-gray-600 border-orange-200 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </button>
                                                        )
                                                    })}
                                                </div>

                                                <button 
                                                    onClick={() => handlePageChange(safeCurrentPage + 1)} 
                                                    disabled={safeCurrentPage === totalPages}
                                                    className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                                >
                                                    <span className="hidden sm:inline">Trang sau</span> <ChevronRight size={16} />
                                                </button>
                                            </div>

                                            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                                <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                                <input 
                                                    key={`menu-page-${safeCurrentPage}`}
                                                    type="number" min={1} max={totalPages} defaultValue={safeCurrentPage}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            const page = parseInt(e.target.value);
                                                            if (page >= 1 && page <= totalPages) handlePageChange(page);
                                                            else e.target.value = safeCurrentPage;
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const page = parseInt(e.target.value);
                                                        if (page >= 1 && page <= totalPages && page !== safeCurrentPage) handlePageChange(page);
                                                        else e.target.value = safeCurrentPage;
                                                    }}
                                                    className="w-16 h-10 px-2 text-center border border-orange-200 rounded-lg outline-none text-sm font-bold text-gray-700 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-all"
                                                    title="Nhập số trang và nhấn Enter"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="bg-gradient-to-b from-orange-50 to-white py-24 min-h-[50vh] rounded-2xl shadow-sm text-center border border-orange-200/60 flex flex-col items-center justify-center">
                                <div className="bg-white p-6 rounded-full mb-4 shadow-sm">
                                    <Search className="text-gray-300 w-16 h-16" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-700">Không tìm thấy sản phẩm!</h3>
                                <p className="text-gray-500 mt-2 mb-6 max-w-md">
                                    Rất tiếc, chúng tôi không tìm thấy sản phẩm nào phù hợp với bộ lọc hiện tại của bạn. Vui lòng thử lại với cấu hình khác.
                                </p>
                                <button onClick={clearAllFilters} className="bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold py-2.5 px-6 rounded-lg transition-colors shadow-sm">
                                    Xóa tất cả bộ lọc
                                </button>
                            </div>
                        )}
                    </section>
                    
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default MenuPage;